import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { HealthAndSafety } from '@mui/icons-material';

import {
  useGetHealthCheckAccessConfig,
  useSaveHealthCheckAccessConfig,
} from '../../hooks/reactQuerys/HealthCheckAccessQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useAlert } from '../../context/AlertContext';
import { filterUsersBySearch, toggleUserId } from './userAccessList';

/**
 * Health Check freischalten (#1283) — unter /admin/dev/healthcheck.
 *
 * Den Health Check beim Speichern eines Vorgangs, den Knopf „Health Check" im
 * Vorgang und den Vorgang-Check in den Systemeinstellungen sehen nur die hier
 * angehakten Personen, und nur bei eingeschaltetem Hauptschalter. Dasselbe
 * Muster wie KI (#1336) und Word-Plugin (#1245): leer heißt niemand.
 */
export const HealthCheckAccessOverview: React.FC = () => {
  const { showAlert } = useAlert();
  const { healthCheckAccessConfig, isLoadingHealthCheckAccessConfig } =
    useGetHealthCheckAccessConfig();
  const { saveHealthCheckAccessConfig, isPendingSaveHealthCheckAccessConfig } =
    useSaveHealthCheckAccessConfig();
  const { users, isLoadingUsers } = useGetUsers();

  const [enabled, setEnabled] = useState(false);
  const [enabledUserArray, setEnabledUserArray] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!healthCheckAccessConfig) {
      return;
    }

    setEnabled(healthCheckAccessConfig.Enabled);
    setEnabledUserArray(healthCheckAccessConfig.EnabledUserArray ?? []);
  }, [healthCheckAccessConfig]);

  const visibleUsers = filterUsersBySearch(users ?? [], userSearch);

  async function handleSave() {
    try {
      await saveHealthCheckAccessConfig({
        Enabled: enabled,
        EnabledUserArray: enabledUserArray,
      });
      showAlert({
        text: 'Die Freischaltung wurde gespeichert.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Die Freischaltung konnte nicht gespeichert werden.',
        severity: 'error',
      });
    }
  }

  if (isLoadingHealthCheckAccessConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <HealthAndSafety />
        <Typography variant='h5'>Health Check</Typography>
      </Stack>

      <Alert severity='info' sx={{ mb: 3 }}>
        Bei freigeschalteten Personen läuft beim Speichern eines Vorgangs der
        Health Check; sie sehen den Knopf „Health Check" im Vorgang und unter
        Systemeinstellungen → Health-Check den „Vorgang-Check". Alle anderen
        bemerken davon nichts — auch keine der früheren Warnungen, die der
        Health Check ersetzt hat. Die Dubletten-Bereinigung bleibt für alle wie
        bisher.
      </Alert>

      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEnabled(event.target.checked)
                }
              />
            }
            label='Health Check aktiv'
          />
          <Typography variant='body2' color='text.secondary'>
            Ausgeschaltet hat niemand den Health Check, auch die unten
            angehakten Personen nicht.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant='subtitle2'>Freigeschaltete Benutzer</Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Der Health Check ist ausschließlich für die hier angehakten Personen
            aktiv.
          </Typography>

          {isLoadingUsers ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size='small'
                  label='Benutzer suchen'
                  value={userSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setUserSearch(event.target.value)
                  }
                  sx={{ minWidth: 260 }}
                />
                <Typography variant='body2' color='text.secondary'>
                  {enabledUserArray.length} von {(users ?? []).length}{' '}
                  freigeschaltet
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                {visibleUsers.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ p: 1 }}
                  >
                    Kein Benutzer gefunden.
                  </Typography>
                ) : (
                  visibleUsers.map(user => (
                    <FormControlLabel
                      key={user.idUser}
                      sx={{ display: 'flex', mr: 0 }}
                      control={
                        <Checkbox
                          size='small'
                          checked={enabledUserArray.includes(user.idUser)}
                          onChange={() =>
                            setEnabledUserArray(previous =>
                              toggleUserId(previous, user.idUser)
                            )
                          }
                        />
                      }
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant='body2'>
                            {user.LastName}, {user.FirstName}
                          </Typography>
                          {Boolean(user.isNotary) && (
                            <Chip size='small' label='Notar' />
                          )}
                          <Typography variant='caption' color='text.secondary'>
                            {user.Email}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </Box>
            </>
          )}

          {enabled && enabledUserArray.length === 0 && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Es ist niemand freigeschaltet — der Health Check ist damit für
              alle Benutzer aus, obwohl der Hauptschalter an ist.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Button
        variant='contained'
        onClick={() => void handleSave()}
        disabled={isPendingSaveHealthCheckAccessConfig}
      >
        {isPendingSaveHealthCheckAccessConfig
          ? 'Wird gespeichert …'
          : 'Speichern'}
      </Button>
    </Box>
  );
};
